<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'programTask',
    'assignProject',
    'openContact',
    'startDictation',
  ],
  setup(props) {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);
    const showMobileMenu = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
      showMobileMenu,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      if (this.isOnPrivateNote) {
        return getAllowedFileTypesByChannel();
      }

      let channelType = this.channelType || this.inbox?.channel_type;
      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;
    },
    closeMobileMenu() {
      this.showMobileMenu = false;
    },
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
  },
};
</script>

<template>
  <div class="reply-bottom-panel flex justify-between p-3" :class="wrapClass">
    <div class="left-wrap desktop-actions">
      <NextButton
        v-if="!isEditorDisabled"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley-sticker"
        slate
        faded
        sm
        @click="toggleEmojiPicker"
      />
      <FileUpload
        v-if="showAttachButton"
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="i-ph-signature"
        slate
        faded
        sm
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="showQuotedReplyToggle"
        v-tooltip.top-end="quotedReplyToggleTooltip"
        icon="i-ph-quotes"
        :variant="quotedReplyEnabled ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="quotedReplyEnabled"
        @click="$emit('toggleQuotedReply')"
      />
      <NextButton
        v-if="enableWhatsAppTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <NextButton
        v-if="enableContentTemplates"
        v-tooltip.top-end="'Content Templates'"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectContentTemplate')"
      />
      <VideoCallButton
        v-if="
          (isAWebWidgetInbox || isAPIInbox) &&
          !isOnPrivateNote &&
          !isEditorDisabled
        "
        :conversation-id="conversationId"
      />
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <NextButton
        v-if="enableInsertArticleInReply"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
    </div>
    <div class="right-wrap desktop-actions">
      <NextButton
        :label="sendButtonText"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      />
    </div>
    <div class="mobile-actions">
      <NextButton
        icon="i-ph-plus"
        slate
        faded
        sm
        class="mobile-plus"
        :aria-expanded="showMobileMenu"
        aria-label="Mostrar opciones"
        @click="toggleMobileMenu"
      />
      <div class="mobile-trailing-actions">
        <NextButton
          v-if="isSendDisabled && showAudioRecorderButton"
          :icon="
            !isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'
          "
          slate
          faded
          sm
          :aria-label="isRecordingAudio ? 'Terminar grabación' : 'Opciones de voz'"
          @click="
            isRecordingAudio
              ? toggleAudioRecorderPlayPause()
              : toggleMobileMenu()
          "
        />
        <NextButton
          v-else
          icon="i-ph-arrow-up-bold"
          type="submit"
          :color="isNote ? 'amber' : 'blue'"
          :disabled="isSendDisabled"
          aria-label="Enviar"
          @click="onSend"
        />
      </div>
      <Transition name="mobile-tray">
        <div v-if="showMobileMenu" class="mobile-attachment-tray">
          <FileUpload
            v-if="showAttachButton"
            input-id="conversationAttachmentMobile"
            :size="4096 * 4096"
            :accept="allowedFileTypes"
            :multiple="enableMultipleFileUpload"
            :drop="enableDragAndDrop"
            :drop-directory="false"
            :data="{
              direct_upload_url: '/rails/active_storage/direct_uploads',
              direct_upload: true,
            }"
            class="tray-option"
            @input-file="onFileUpload"
            @click="closeMobileMenu"
          >
            <span class="tray-icon tray-icon--blue">
              <i class="i-ph-images-square-fill" />
            </span>
            <span>Fotos</span>
          </FileUpload>
          <button type="button" class="tray-option" title="Próximamente">
            <span class="tray-icon tray-icon--slate"
              ><i class="i-ph-camera-fill"
            /></span>
            <span>Cámara</span>
          </button>
          <button type="button" class="tray-option" title="Próximamente">
            <span class="tray-icon tray-icon--indigo"
              ><i class="i-ph-file-text-fill"
            /></span>
            <span>Documento</span>
          </button>
          <button
            v-if="showAudioRecorderButton"
            type="button"
            class="tray-option"
            @click="
              toggleAudioRecorder();
              closeMobileMenu();
            "
          >
            <span class="tray-icon tray-icon--orange"
              ><i class="i-ph-microphone-fill"
            /></span>
            <span>Enviar audio</span>
          </button>
          <button
            type="button"
            class="tray-option"
            @click="
              $emit('startDictation');
              closeMobileMenu();
            "
          >
            <span class="tray-icon tray-icon--green"
              ><i class="i-ph-text-aa-fill"
            /></span>
            <span>Dictar texto</span>
          </button>
          <button
            type="button"
            class="tray-option"
            @click="
              $emit('programTask');
              closeMobileMenu();
            "
          >
            <span class="tray-icon tray-icon--red"
              ><i class="i-ph-calendar-check-fill"
            /></span>
            <span>Programar tarea</span>
          </button>
          <button
            type="button"
            class="tray-option"
            @click="
              $emit('assignProject');
              closeMobileMenu();
            "
          >
            <span class="tray-icon tray-icon--purple"
              ><i class="i-ph-kanban-fill"
            /></span>
            <span>Asignar proyecto</span>
          </button>
          <button
            type="button"
            class="tray-option"
            @click="
              $emit('openContact');
              closeMobileMenu();
            "
          >
            <span class="tray-icon tray-icon--green"
              ><i class="i-ph-user-circle-fill"
            /></span>
            <span>Contacto</span>
          </button>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  @apply flex;
}

.mobile-actions {
  @apply hidden;
}

@media (max-width: 767px) {
  .reply-bottom-panel {
    display: contents;
  }

  .desktop-actions {
    display: none;
  }

  .mobile-actions {
    display: contents;
  }

  .mobile-plus {
    grid-column: 1;
    grid-row: 1;
    align-self: center;
  }

  .mobile-trailing-actions {
    grid-column: 3;
    grid-row: 1;
    align-self: center;
    display: flex;
  }

  .mobile-attachment-tray {
    grid-column: 1 / -1;
    grid-row: 2;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem 0.5rem;
    margin: 0.5rem -0.5rem -0.375rem;
    padding: 1rem 0.625rem max(1rem, env(safe-area-inset-bottom));
    border-top: 1px solid #e5e7eb;
    border-radius: 1.5rem 1.5rem 0 0;
    background: #f7f7f8;
    box-shadow: 0 -8px 28px rgb(0 0 0 / 10%);
  }

  .tray-option {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    border: 0;
    background: transparent;
    color: #18181b;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.1;
    text-align: center;
  }

  :deep(.tray-option label) {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
  }

  .tray-icon {
    display: grid;
    width: 3rem;
    height: 3rem;
    place-items: center;
    border-radius: 1rem;
    background: #fff;
    box-shadow: 0 1px 5px rgb(0 0 0 / 8%);
    font-size: 1.55rem;
  }

  .tray-icon--blue {
    color: #1687f8;
  }
  .tray-icon--slate {
    color: #374151;
  }
  .tray-icon--indigo {
    color: #5965db;
  }
  .tray-icon--orange {
    color: #f59e0b;
  }
  .tray-icon--red {
    color: #ef476f;
  }
  .tray-icon--purple {
    color: #8b5cf6;
  }
  .tray-icon--amber {
    color: #d97706;
  }
  .tray-icon--green {
    color: #10b981;
  }

  .mobile-tray-enter-active,
  .mobile-tray-leave-active {
    transition:
      opacity 160ms ease,
      transform 160ms ease;
  }

  .mobile-tray-enter-from,
  .mobile-tray-leave-to {
    opacity: 0;
    transform: translateY(0.75rem);
  }

  :deep(.mobile-plus button),
  .mobile-trailing-actions :deep(button) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    border-color: #111 !important;
    background: #111 !important;
    color: #fff !important;
  }
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
